import { useMutation } from "@tanstack/react-query";
import { homesOnboardingApi } from "libs/api";
import type {
    ValidateOnboardingInputUrlResponseMessageErrorMessageEnum,
    ValidateOnboardingInputUrlResponseMessageUrlTypeEnum,
} from "libs/codegen/homes-onbaording";
import { useState } from "react";
import { useNavigate, useParams } from "router";
import { PropertyType, UrlType, ValidationError } from "./constants";
import { useURLInputValue } from "./urlInputAtom";

export const useURLInputHandler = () => {
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic");
    const inputURL = useURLInputValue();
    const navigate = useNavigate();

    type Response = {
        data: {
            isSuccess: boolean;
            urlType?: ValidateOnboardingInputUrlResponseMessageUrlTypeEnum;
            errorMessage: ValidateOnboardingInputUrlResponseMessageErrorMessageEnum;
        };
    };

    type ErrorResponse = {
        response: Response;
    };

    const [validationError, setValidationError] = useState<ValidationError | null>(null);

    const { mutate, isLoading } = useMutation<Response, ErrorResponse, string>(
        (url) => homesOnboardingApi.validateAirbnbInputUrl(lang, url),
        {
            onSuccess: (data) => {
                if (data.data.urlType.toString() === UrlType.Airbnb.toString()) {
                    navigate("/:lang/list-my-property/get-started/automatic/lists", {
                        params: {
                            lang,
                        },
                    });
                } else {
                    sessionStorage.setItem("bcomUrl", inputURL.sanitizedUrl);
                    window.location.assign(`/homes/${lang}/onboarding?propertyType=${PropertyType.HOTEL_AND_MORE}`);
                }
            },
            onError: (error) => {
                if (error.response?.data === undefined) setValidationError(ValidationError.TechnicalIssue);
                else if (error.response.data.errorMessage.toString() === ValidationError.NoURLEntered.toString())
                    setValidationError(ValidationError.NoURLEntered);
                else if (error.response.data.errorMessage.toString() === ValidationError.InvalidURLEntered.toString())
                    setValidationError(ValidationError.InvalidURLEntered);
                else if (error.response.data.errorMessage.toString() === ValidationError.IncorrectUrl.toString())
                    setValidationError(ValidationError.IncorrectUrl);
                else if (
                    error.response.data.errorMessage.toString() === ValidationError.InProgressBatchExist.toString()
                )
                    setValidationError(ValidationError.InProgressBatchExist);
                else setValidationError(ValidationError.TechnicalIssue);
            },
        },
    );

    // when clicking back, go back to previous step.
    const onBack = () => {
        navigate("/:lang/list-my-property/get-started", {
            params: { lang },
        });
    };

    // send to backend to validate url and logging.
    // if url correct, go to next step.
    const onNext = () => {
        setValidationError(null);
        mutate(inputURL.sanitizedUrl);
    };

    return {
        validationError,
        setValidationError,
        loading: isLoading,
        onNext,
        onBack,
    };
};
